import { useState, useEffect } from 'react';
import { toast } from 'react-toastify';
import { doc, getDoc, setDoc, updateDoc } from 'firebase/firestore';
import { db } from '../../firebase/config';
import { useAuth } from '../../context/AuthContext';
import { useLanguage } from '../../context/LanguageContext';
import ThumbnailUpload from '../../components/UI/ThumbnailUpload';

interface ProviderProfileData {
  companyName: string;
  contactName: string;
  phoneNumber: string;
  phoneCountryCode: string;
  streetAddress: string;
  zipCode: string;
  city: string;
  country: string;
  countryOfIncorporation: string;
  preferredLanguage: string;
  employees: string;
  naceCode: string;
  skills: string[];
  shortDescription: string;
  longDescription: string;
  // New thumbnail fields
  userPhoto?: string;
  companyLogo?: string;
}

const ProviderProfile = () => {
  const { currentUser } = useAuth();
  const { t } = useLanguage();
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [skillInput, setSkillInput] = useState('');
  
  const [profile, setProfile] = useState<ProviderProfileData>({
    companyName: '',
    contactName: '',
    phoneNumber: '',
    phoneCountryCode: '+33',
    streetAddress: '',
    zipCode: '',
    city: '',
    country: '',
    preferredLanguage: '',
    employees: '',
    naceCode: '',
    skills: [],
    shortDescription: '',
    longDescription: '',
    userPhoto: '',
    companyLogo: ''
  });

  // Major European countries
  const topCountries = [
    { code: 'FR', name: 'France' },
    { code: 'LU', name: 'Luxembourg' },
    { code: 'DE', name: 'Germany' },
    { code: 'GB', name: 'United Kingdom' },
    { code: 'IT', name: 'Italy' },
    { code: 'ES', name: 'Spain' },
    { code: 'NL', name: 'Netherlands' },
    { code: 'BE', name: 'Belgium' },
    { code: 'SE', name: 'Sweden' },
    { code: 'PL', name: 'Poland' },
    { code: 'AT', name: 'Austria' }
  ];

  // All European countries including small ones
  const allEuropeanCountries = [
    { code: 'AD', name: 'Andorra' },
    { code: 'AL', name: 'Albania' },
    { code: 'AT', name: 'Austria' },
    { code: 'BA', name: 'Bosnia and Herzegovina' },
    { code: 'BE', name: 'Belgium' },
    { code: 'BG', name: 'Bulgaria' },
    { code: 'BY', name: 'Belarus' },
    { code: 'CH', name: 'Switzerland' },
    { code: 'CY', name: 'Cyprus' },
    { code: 'CZ', name: 'Czech Republic' },
    { code: 'DE', name: 'Germany' },
    { code: 'DK', name: 'Denmark' },
    { code: 'EE', name: 'Estonia' },
    { code: 'ES', name: 'Spain' },
    { code: 'FI', name: 'Finland' },
    { code: 'FR', name: 'France' },
    { code: 'GB', name: 'United Kingdom' },
    { code: 'GE', name: 'Georgia' },
    { code: 'GR', name: 'Greece' },
    { code: 'HR', name: 'Croatia' },
    { code: 'HU', name: 'Hungary' },
    { code: 'IE', name: 'Ireland' },
    { code: 'IS', name: 'Iceland' },
    { code: 'IT', name: 'Italy' },
    { code: 'LI', name: 'Liechtenstein' },
    { code: 'LT', name: 'Lithuania' },
    { code: 'LU', name: 'Luxembourg' },
    { code: 'LV', name: 'Latvia' },
    { code: 'MC', name: 'Monaco' },
    { code: 'MD', name: 'Moldova' },
    { code: 'ME', name: 'Montenegro' },
    { code: 'MK', name: 'North Macedonia' },
    { code: 'MT', name: 'Malta' },
    { code: 'NL', name: 'Netherlands' },
    { code: 'NO', name: 'Norway' },
    { code: 'PL', name: 'Poland' },
    { code: 'PT', name: 'Portugal' },
    { code: 'RO', name: 'Romania' },
    { code: 'RS', name: 'Serbia' },
    { code: 'RU', name: 'Russia' },
    { code: 'SE', name: 'Sweden' },
    { code: 'SI', name: 'Slovenia' },
    { code: 'SK', name: 'Slovakia' },
    { code: 'SM', name: 'San Marino' },
    { code: 'TR', name: 'Turkey' },
    { code: 'UA', name: 'Ukraine' },
    { code: 'VA', name: 'Vatican City' },
    { code: 'XK', name: 'Kosovo' }
  ];

  // Rest of the world countries
  const otherCountries = [
    { code: 'US', name: 'United States' },
    { code: 'CA', name: 'Canada' },
    { code: 'JP', name: 'Japan' },
    { code: 'CN', name: 'China' },
    { code: 'IN', name: 'India' },
    { code: 'AU', name: 'Australia' },
    { code: 'BR', name: 'Brazil' },
    { code: 'ZA', name: 'South Africa' },
    { code: 'AE', name: 'United Arab Emirates' },
    { code: 'SG', name: 'Singapore' },
    { code: 'KR', name: 'South Korea' },
    { code: 'IL', name: 'Israel' }
  ];

  // Only French and English for now
  const languages = [
    { code: 'en', name: 'English' },
    { code: 'fr', name: 'Français' }
  ];
  
  useEffect(() => {
    if (!currentUser) return;
    
    const fetchProfile = async () => {
      try {
        setLoading(true);
        const docRef = doc(db, 'providerProfiles', currentUser.uid);
        const docSnap = await getDoc(docRef);
        
        if (docSnap.exists()) {
          setProfile(docSnap.data() as ProviderProfileData);
        }
      } catch (error) {
        console.error('Error fetching profile:', error);
        toast.error(t('error.loading'));
      } finally {
        setLoading(false);
      }
    };
    
    fetchProfile();
  }, [currentUser, t]);
  
  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setProfile(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleUserPhotoChange = (imageDataUrl: string | null) => {
    setProfile(prev => ({
      ...prev,
      userPhoto: imageDataUrl || ''
    }));
  };

  const handleCompanyLogoChange = (imageDataUrl: string | null) => {
    setProfile(prev => ({
      ...prev,
      companyLogo: imageDataUrl || ''
    }));
  };
  
  const addSkill = () => {
    if (!skillInput.trim()) return;
    
    const newSkills = skillInput
      .split(',')
      .map(skill => skill.trim())
      .filter(skill => skill.length > 0);
    
    const uniqueNewSkills = newSkills.filter(skill => !profile.skills.includes(skill));
    
    if (uniqueNewSkills.length > 0) {
      setProfile(prev => ({
        ...prev,
        skills: [...prev.skills, ...uniqueNewSkills]
      }));
      setSkillInput('');
    } else {
      toast.info(t('profile.skillsAlreadyAdded'));
    }
  };
  
  const removeSkill = (skillToRemove: string) => {
    setProfile(prev => ({
      ...prev,
      skills: prev.skills.filter(skill => skill !== skillToRemove)
    }));
  };
  
  const handleSkillKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      addSkill();
    }
  };
  
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!currentUser) return;
    
    if (profile.skills.length < 3) {
      toast.error(t('profile.addAtLeastThreeSkills'));
      return;
    }
    
    try {
      setSaving(true);
      const docRef = doc(db, 'providerProfiles', currentUser.uid);
      const docSnap = await getDoc(docRef);
      
      if (docSnap.exists()) {
        await updateDoc(docRef, { ...profile });
      } else {
        await setDoc(docRef, {
          ...profile,
          userId: currentUser.uid,
          createdAt: new Date()
        });
      }
      
      toast.success(t('success.saved'));
    } catch (error) {
      console.error('Error saving profile:', error);
      toast.error(t('error.saving'));
    } finally {
      setSaving(false);
    }
  };
  
  if (loading) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-orange-500"></div>
      </div>
    );
  }
  
  return (
    <div className="container mx-auto px-4 py-8 max-w-2xl">
      <div className="bg-white rounded-lg shadow-md p-6">
        <h1 className="text-2xl font-bold mb-6">{t('profile.providerProfile')}</h1>
        
        <form onSubmit={handleSubmit} className="space-y-6">
          {/* Profile Images Section */}
          <div>
            <h2 className="text-lg font-semibold mb-4 pb-2 border-b">{t('profile.images')}</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <ThumbnailUpload
                type="user"
                currentImage={profile.userPhoto}
                onImageChange={handleUserPhotoChange}
                disabled={saving}
              />
              <ThumbnailUpload
                type="company"
                currentImage={profile.companyLogo}
                onImageChange={handleCompanyLogoChange}
                disabled={saving}
              />
            </div>
          </div>

          {/* Company Information */}
          <div>
            <h2 className="text-lg font-semibold mb-4 pb-2 border-b">{t('profile.companyInfo')}</h2>
            <div className="space-y-4">
              <div>
                <label htmlFor="companyName" className="block text-sm font-medium text-gray-700 mb-1">
                  {t('profile.companyName')} *
                </label>
                <input
                  type="text"
                  id="companyName"
                  name="companyName"
                  value={profile.companyName}
                  onChange={handleChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-orange-500"
                  required
                />
              </div>
              
              <div>
                <label htmlFor="contactName" className="block text-sm font-medium text-gray-700 mb-1">
                  {t('profile.contactName')} *
                </label>
                <input
                  type="text"
                  id="contactName"
                  name="contactName"
                  value={profile.contactName}
                  onChange={handleChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-orange-500"
                  required
                />
              </div>
              
              <div>
                <label htmlFor="phoneNumber" className="block text-sm font-medium text-gray-700 mb-1">
                  {t('profile.phoneNumber')} *
                </label>
                <div className="flex">
                  <select
                    id="phoneCountryCode"
                    name="phoneCountryCode"
                    value={profile.phoneCountryCode}
                    onChange={handleChange}
                    className="w-24 px-3 py-2 border border-gray-300 rounded-l-md focus:outline-none focus:ring-2 focus:ring-orange-500"
                    required
                  >
                    <option value="+33">+33</option>
                    <option value="+32">+32</option>
                    <option value="+49">+49</option>
                    <option value="+34">+34</option>
                    <option value="+39">+39</option>
                    <option value="+44">+44</option>
                  </select>
                  <input
                    type="tel"
                    id="phoneNumber"
                    name="phoneNumber"
                    value={profile.phoneNumber}
                    onChange={handleChange}
                    className="flex-1 px-3 py-2 border border-gray-300 rounded-r-md focus:outline-none focus:ring-2 focus:ring-orange-500"
                    required
                  />
                </div>
              </div>

              {/* Address Fields */}
              <div className="space-y-4">
                <div>
                  <label htmlFor="streetAddress" className="block text-sm font-medium text-gray-700 mb-1">
                    {t('profile.streetAddress')} *
                  </label>
                  <input
                    type="text"
                    id="streetAddress"
                    name="streetAddress"
                    value={profile.streetAddress}
                    onChange={handleChange}
                    placeholder={t('profile.streetAddressPlaceholder')}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-orange-500"
                    required
                  />
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label htmlFor="zipCode" className="block text-sm font-medium text-gray-700 mb-1">
                      {t('profile.zipCode')} *
                    </label>
                    <input
                      type="text"
                      id="zipCode"
                      name="zipCode"
                      value={profile.zipCode}
                      onChange={handleChange}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-orange-500"
                      required
                    />
                  </div>

                  <div>
                    <label htmlFor="city" className="block text-sm font-medium text-gray-700 mb-1">
                      {t('profile.city')} *
                    </label>
                    <input
                      type="text"
                      id="city"
                      name="city"
                      value={profile.city}
                      onChange={handleChange}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-orange-500"
                      required
                    />
                  </div>
                </div>
              </div>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label htmlFor="countryOfIncorporation" className="block text-sm font-medium text-gray-700 mb-1">
                    {t('profile.countryOfIncorporation')} *
                  </label>
                  <select
                    id="countryOfIncorporation"
                    name="countryOfIncorporation"
                    value={profile.countryOfIncorporation}
                    onChange={handleChange}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-orange-500"
                    required
                  >
                    <option value="">{t('profile.selectCountry')}</option>
                    
                    <optgroup label="European Countries">
                      {allEuropeanCountries.map(country => (
                        <option key={country.code} value={country.code}>
                          {country.name}
                        </option>
                      ))}
                    </optgroup>
                    
                    <optgroup label={t('profile.otherCountries')}>
                      {otherCountries.map(country => (
                        <option key={country.code} value={country.code}>
                          {country.name}
                        </option>
                      ))}
                    </optgroup>
                  </select>
                </div>
                
                <div>
                  <label htmlFor="employees" className="block text-sm font-medium text-gray-700 mb-1">
                    {t('profile.employees')} *
                  </label>
                  <select
                    id="employees"
                    name="employees"
                    value={profile.employees}
                    onChange={handleChange}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-orange-500"
                    required
                  >
                    <option value="">{t('profile.selectEmployees')}</option>
                    <option value="1">{t('profile.justMe')}</option>
                    <option value="2-5">2-5</option>
                    <option value="6-10">6-10</option>
                    <option value="11-50">11-50</option>
                    <option value="51-200">51-200</option>
                    <option value="201+">201+</option>
                  </select>
                </div>
              </div>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label htmlFor="naceCode" className="block text-sm font-medium text-gray-700 mb-1">
                    {t('profile.naceCode')} *
                  </label>
                  <input
                    type="text"
                    id="naceCode"
                    name="naceCode"
                    value={profile.naceCode}
                    onChange={handleChange}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-orange-500"
                    required
                  />
                </div>
                
                <div>
                  <label htmlFor="country" className="block text-sm font-medium text-gray-700 mb-1">
                    {t('profile.country')} *
                  </label>
                  <select
                    id="country"
                    name="country"
                    value={profile.country}
                    onChange={handleChange}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-orange-500"
                    required
                  >
                    <option value="">{t('profile.selectCountry')}</option>
                    
                    <optgroup label="European Countries">
                      {allEuropeanCountries.map(country => (
                        <option key={country.code} value={country.code}>
                          {country.name}
                        </option>
                      ))}
                    </optgroup>
                    
                    <optgroup label={t('profile.otherCountries')}>
                      {otherCountries.map(country => (
                        <option key={country.code} value={country.code}>
                          {country.name}
                        </option>
                      ))}
                    </optgroup>
                  </select>
                </div>
              </div>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div></div>

                <div>
                  <label htmlFor="preferredLanguage" className="block text-sm font-medium text-gray-700 mb-1">
                    {t('profile.preferredLanguage')} *
                  </label>
                  <select
                    id="preferredLanguage"
                    name="preferredLanguage"
                    value={profile.preferredLanguage}
                    onChange={handleChange}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-orange-500"
                    required
                  >
                    <option value="">{t('profile.selectLanguage')}</option>
                    {languages.map(language => (
                      <option key={language.code} value={language.code}>
                        {language.name}
                      </option>
                    ))}
                  </select>
                </div>
              </div>
            </div>
          </div>
          
          {/* Skills and Expertise */}
          <div>
            <h2 className="text-lg font-semibold mb-4 pb-2 border-b">{t('profile.skillsExpertise')}</h2>
            <div className="space-y-4">
              <div>
                <label htmlFor="skills" className="block text-sm font-medium text-gray-700 mb-1">
                  {t('profile.skills')} ({t('profile.addSkills')}) * - {t('profile.separateSkills')}
                </label>
                <div className="flex">
                  <input
                    type="text"
                    id="skills"
                    value={skillInput}
                    onChange={(e) => setSkillInput(e.target.value)}
                    onKeyDown={handleSkillKeyDown}
                    placeholder={t('profile.addSkillsPlaceholder')}
                    className="flex-1 px-3 py-2 border border-gray-300 rounded-l-md focus:outline-none focus:ring-2 focus:ring-orange-500"
                  />
                  <button
                    type="button"
                    onClick={addSkill}
                    className="px-4 py-2 bg-orange-500 text-white rounded-r-md hover:bg-orange-600 transition-colors"
                  >
                    {t('profile.addButton')}
                  </button>
                </div>
                
                <div className="flex flex-wrap gap-2 mt-2">
                  {profile.skills.map((skill, index) => (
                    <div
                      key={index}
                      className="bg-orange-100 text-orange-700 px-3 py-1 rounded-full flex items-center"
                    >
                      <span>{skill}</span>
                      <button
                        type="button"
                        onClick={() => removeSkill(skill)}
                        className="ml-2 text-orange-500 hover:text-orange-700"
                      >
                        &times;
                      </button>
                    </div>
                  ))}
                </div>
                
                {profile.skills.length < 3 && (
                  <p className="text-red-500 text-sm mt-1">{t('profile.addAtLeastThreeSkills')}</p>
                )}
              </div>
              
              <div>
                <label htmlFor="shortDescription" className="block text-sm font-medium text-gray-700 mb-1">
                  {t('profile.shortDescription')} * ({t('profile.maxCharacters')})
                </label>
                <textarea
                  id="shortDescription"
                  name="shortDescription"
                  value={profile.shortDescription}
                  onChange={handleChange}
                  rows={3}
                  maxLength={200}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-orange-500"
                  required
                ></textarea>
                <p className="text-xs text-gray-500 mt-1">
                  {profile.shortDescription.length}/200 {t('profile.characters')}
                </p>
              </div>
              
              <div>
                <label htmlFor="longDescription" className="block text-sm font-medium text-gray-700 mb-1">
                  {t('profile.detailedDescription')} *
                </label>
                <textarea
                  id="longDescription"
                  name="longDescription"
                  value={profile.longDescription}
                  onChange={handleChange}
                  rows={6}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-orange-500"
                  required
                ></textarea>
              </div>
            </div>
          </div>
          
          {/* Submit Button */}
          <button
            type="submit"
            className="w-full bg-orange-500 text-white py-2 rounded-md font-medium hover:bg-orange-600 transition-colors"
            disabled={saving || profile.skills.length < 3}
          >
            {saving ? (
              <span className="flex items-center justify-center">
                <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                </svg>
                {t('profile.saving')}
              </span>
            ) : (
              t('profile.saveProfile')
            )}
          </button>
        </form>
      </div>
    </div>
  );
};

export default ProviderProfile;